/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { LoadingOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency } from '@sportninja/common/currency'
import Price from 'src/components/ts-components/Price'
import { InfoTooltip } from 'src/components/ts-components/RichTooltip'
import { weightSemiBold } from 'src/components/css'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

type BalanceSummaryCardsProps = {
  /**
   * The DIVISION FEE, in minor units, BEFORE tax — what the allocation
   * differential measures the shares against.
   *
   * James, Jul 31: "this number should be a hundred percent driven off of what
   * the admin has set for the team on that config screen." It used to be the sum
   * of the PLAYER SHARES, which drifts the moment a rep under- or over-allocates:
   * allocate $1,912 of a $2,000 fee and the card called $1,912 "100% collected".
   * Summed across the visible registrations on the All-Divisions view, under the
   * same single-currency guard as every other figure here.
   */
  teamCost: number
  /**
   * Tax on top — the denominator for "% collected": what the team owes in cash
   * INCLUDING tax (server `kpis.expected_total`, summed like `teamCost`). Paid
   * money includes tax, so measuring it against the pre-tax fee read past 100% on
   * a team that was merely paid up.
   */
  amountDue: number
  /**
   * The part of `amountDue` that is tax (server `kpis.expected_tax`), in minor
   * units. Null when the server does not report it; the tax line is then omitted
   * rather than guessed.
   */
  tax?: number | null
  /** Server KPI `allocated`, summed over the visible registrations (minor units). */
  allocated: number
  /**
   * Server KPI `unallocated` (= team_cost − allocated), summed the same way.
   * Deliberately NOT recomputed here: the server already owns this arithmetic
   * (its balance ledger), and a second implementation is how the card and
   * the sheet start disagreeing by a penny.
   */
  unallocated: number
  /**
   * Minor units (cents), from the server's KPIs for the registrations on screen
   * (`headerMoney`) — never summed from the sheet's rows, so the Show filter
   * cannot change them. `owed` is negative when the team holds a credit.
   */
  paid: number
  owed: number
  currency: string
  /** Suppressed when the visible rows mix currencies — a summed total would be a lie. */
  showMoney: boolean
  /**
   * A payload refetch is in flight. These figures are server-derived, so during
   * a refetch they are momentarily STALE — say so rather than showing an old
   * number as if it were current.
   */
  refreshing?: boolean
  /**
   * SN-4709 (Inon, Aug 12) — render as a STRIP INSIDE BalanceHeaderPanel rather
   * than as a free-floating card: no border, no radius and no surface of its own,
   * because the panel already supplies all three. The standalone card form is
   * kept as the default so the component can still be used on its own.
   */
  embedded?: boolean
  /**
   * The figures WITHOUT the title row or the track, for the single-registration
   * header (SN-8310), where the milestone track above already shows progress.
   * Adds the TEAM TOTAL figure, since there is no title line to state it.
   *
   * The figures keep their full size: what made collection progress "the more
   * important indicator" (Inon, Aug 12) was these numbers, not the bar.
   */
  figuresOnly?: boolean
}

/**
 * Same elevation language as the sheet below and as RegistrationTable: a raised
 * SECONDARY_300 surface with a SECONDARY_100 edge. SECONDARY_400 on a
 * SECONDARY_500 page is a 3-hex-point difference — the card reads as a hairline
 * outline floating on nothing rather than a panel.
 */
const card = css`
  flex: 1 1 260px;
  min-width: 240px;
  padding: 16px 20px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 10px;
  background: ${colors.SECONDARY_300};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

/**
 * The embedded half: same content, none of the chrome. The parent panel supplies
 * the surface and the border, and these are GRID ITEMS of that panel (the
 * wrapper is `display: contents`), which is what puts their seam on the same
 * column boundary as the regions above rather than wherever flex would land it.
 */
const embeddedCard = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const titleSpinner = css`
  color: ${colors.PRIMARY};
  font-size: 11px;
  margin-left: 6px;
`

const cardTitle = css`
  ${weightSemiBold};
  font-size: 11px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_70};
`

const subtle = css`
  font-size: 13px;
  color: ${colors.NEUTRAL_70};
`

/**
 * A single filled/empty track. Width is a clamped percentage, never a raw ratio.
 *
 * Always money green: every remaining caller is measuring collection.
 */
const Track = ({
  percent,
  height = 8,
}: {
  percent: number
  height?: number
}) => (
  <div
    css={css`
      width: 100%;
      height: ${height}px;
      border-radius: 999px;
      /* The UNFILLED remainder. It sits on a raised SECONDARY_300 surface now
         (card and grid row alike), so it has to be lighter than that to read. */
      background: ${colors.SECONDARY_100};
      overflow: hidden;
    `}
  >
    <div
      css={css`
        width: ${Math.max(0, Math.min(100, percent))}%;
        height: 100%;
        border-radius: 999px;
        background: ${colors.SUCCESS_GREEN};
        transition: width 0.2s ease-in-out;
      `}
    />
  </div>
)

/** A caption and, where it has one, its info affordance. */
const captionRow = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

/** The label under one of the paired money figures. */
const figureCaption = css`
  ${weightSemiBold};
  font-size: 13px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_70};
`

/**
 * One figure of the money readout. Paid and Outstanding are PEERS — same type
 * size, same treatment, told apart by colour — and sit side by side (SN-8310,
 * Jeff: reading them used to be "a spirit quest across the screen").
 *
 * `tip` puts a "what is this?" beside the caption, for a figure whose meaning
 * the label alone cannot carry.
 */
const MoneyFigure = ({
  amount,
  currency,
  color,
  label,
  testId,
  tip,
}: {
  amount: number
  currency: string
  color: string
  label: string
  testId: string
  tip?: { title: string; body: string }
}) => (
  <div
    data-testid={testId}
    css={css`
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-start;
      min-width: 0;
    `}
  >
    <span
      css={css`
        ${weightSemiBold};
        font-size: 20px;
        line-height: 1;
        font-variant-numeric: tabular-nums;
        color: ${color};
      `}
    >
      <Price amount={amount} currency={currency} />
    </span>
    <span css={captionRow}>
      <span css={figureCaption}>{label}</span>
      {tip ? (
        <InfoTooltip
          title={tip.title}
          body={tip.body}
          placement='bottom'
          maxWidth={300}
          size={12}
        />
      ) : null}
    </span>
  </div>
)

/**
 * The at-a-glance money card above the sheet.
 *
 * It answers the question a rep opens this page with, which the row-level
 * spreadsheet cannot: a table of amounts does not tell you "are we on track?".
 *
 * SN-8310 — the figures are the SERVER's (`headerMoney`), not sums over the
 * rows on screen. Summing the rows let the Show filter change what the team
 * owes, hid the money when a filter matched nothing, and missed money paid by
 * released players. The grid's own total row still follows the filters.
 *
 * U1 — Collected and Outstanding used to be a big figure in one card and a big
 * figure in ANOTHER card, each also restated in a legend between them: four
 * renderings of two numbers. They are one fact — a total, split — so they share
 * one card, as the two ends of the bar that relates them: same type size, same
 * weight, told apart by colour and by which side of the row they sit on. Neither
 * is subordinate to the other; a rep chasing money reads Outstanding first, and
 * demoting it to a caption under Collected would have been the wrong answer to
 * the same complaint.
 *
 * The ROSTER FILLED half is gone (Sep 2): the grid's total row states the roster
 * count, in bold (SN-8310). What is left owns exactly one subject: money.
 */
const BalanceSummaryCards = ({
  teamCost,
  amountDue,
  tax = null,
  allocated,
  unallocated,
  paid,
  owed,
  currency,
  showMoney,
  refreshing = false,
  embedded = false,
  figuresOnly = false,
}: BalanceSummaryCardsProps) => {
  // A spinner on the card title: the figure below it is server-derived, so while
  // a refetch is in flight the number on screen is the PREVIOUS answer.
  const busy = refreshing ? (
    <LoadingOutlined aria-label='refreshing' css={titleSpinner} />
  ) : null
  // Against the cash the team owes, tax included — never the pre-tax fee or the
  // sum of the shares. See `amountDue`.
  const percentCollected =
    amountDue > 0 ? Math.round((paid / amountDue) * 100) : 0
  /*
   * Tax on top, stated once as the equation it is: the pre-tax amount, the tax
   * added to it, and the total every figure on this card is measured against.
   * All three are the server's (the pre-tax side is its total less its tax), so
   * the line always adds up — including over-allocated sheets and invoices issued
   * before tax moved on top, where the pre-tax side is not the team fee.
   */
  const taxLine =
    showMoney && tax !== null && tax > 0
      ? balanceText('common:balanceCardTaxEquation', {
          preTax: formatCurrency({
            amount: amountDue - tax,
            currencyCode: currency,
          }),
          tax: formatCurrency({ amount: tax, currencyCode: currency }),
          total: formatCurrency({ amount: amountDue, currencyCode: currency }),
        })
      : null
  // James also asked for "a differential of the 1912 versus 2000": the gap is the
  // rep's to close, and a card that only shows a percentage hides it entirely.
  // Shown only when there IS one, so a fully allocated sheet stays quiet.
  const showDifferential = showMoney && teamCost > 0 && unallocated !== 0
  const overAllocated = unallocated < 0
  // A negative balance is money paid OVER the fee: say "credit", not "-$50.00
  // outstanding".
  const credit = owed < 0

  const cardStyle = embedded ? embeddedCard : card

  return (
    <div
      css={
        embedded
          ? /* The wrapper has no box of its own — the card takes the region
               BalanceHeaderPanel put it in rather than nesting a second box
               inside one. */
            css`
              display: contents;
            `
          : css`
              display: flex;
              flex-wrap: wrap;
              gap: 16px;
            `
      }
      data-testid='balance-summary-cards'
    >
      <div css={cardStyle}>
        {/* No title row in the header's figures: the banner and the milestone
            track above already say where the money stands. */}
        {figuresOnly ? null : (
          <div
            css={css`
              display: flex;
              align-items: baseline;
              justify-content: space-between;
              gap: 4px 8px;
              flex-wrap: wrap;
            `}
          >
            <span css={cardTitle}>
              {balanceLabel('common:balanceCardCollection')}
              {busy}
            </span>
            {showMoney ? (
              /*
               * The percentage AND the figure it is a percentage OF. Paid and
               * Outstanding below are exactly the two halves of this number
               * (owed == amount − paid, summed), so without it the reader has to
               * add the two figures together to learn what the season is worth —
               * which is arithmetic the card should be doing for them.
               */
              <span css={subtle} data-testid='summary-total'>
                {balanceLabel('common:balanceCardCollected', {
                  percent: percentCollected,
                })}
                {' · '}
                <Price amount={amountDue} currency={currency} />{' '}
                {balanceLabel('common:balanceCardTotal')}
              </span>
            ) : null}
          </div>
        )}

        {showMoney ? (
          <>
            {/* The bar first: it is the relationship, and the figures below are
                its parts. Omitted in the header, where the milestone track
                carries the progress. */}
            {figuresOnly ? null : (
              <Track percent={percentCollected} height={10} />
            )}
            <div
              css={css`
                display: flex;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 8px 24px;
                /* Wraps rather than colliding at five- or six-digit figures. */
                flex-wrap: wrap;
              `}
            >
              <MoneyFigure
                testId='summary-collected'
                amount={paid}
                currency={currency}
                color={colors.SUCCESS_GREEN}
                label={balanceLabel('common:balanceCardPaid')}
              />
              {/* SN-8425 — this is the LEAGUE's figure (fee plus tax, less
                  everything paid), so it counts shares not yet invoiced and need
                  not equal the players' Balance Due summed; the tip says so. */}
              <MoneyFigure
                testId='summary-outstanding'
                amount={Math.abs(owed)}
                currency={currency}
                /* Nothing owed is not a warning: $0.00 reads neutral, like the
                   Team Total (Inon, Sep 24). */
                color={
                  credit
                    ? colors.SUCCESS_GREEN
                    : owed === 0
                    ? colors.NEUTRAL_30
                    : colors.WARNING_300
                }
                label={balanceLabel(
                  credit
                    ? 'common:balanceCardCredit'
                    : 'common:balanceCardOutstanding'
                )}
                tip={{
                  title: balanceLabel(
                    credit
                      ? 'common:balanceCardCredit'
                      : 'common:balanceCardOutstanding'
                  ),
                  body: balanceText('common:balanceCardOutstandingTip'),
                }}
              />
              {/* The header's figures row has no title line, so the total they
                  are parts of is stated here instead. */}
              {figuresOnly ? (
                <MoneyFigure
                  testId='summary-team-total'
                  amount={amountDue}
                  currency={currency}
                  color={colors.NEUTRAL_30}
                  label={balanceLabel('common:balanceCardTeamTotal')}
                />
              ) : null}
            </div>
            {taxLine ? (
              <span css={subtle} data-testid='summary-tax'>
                {taxLine}
              </span>
            ) : null}
            {showDifferential ? (
              /* "$1,912.00 allocated of $2,000.00 · $88.00 unallocated".
                 WARNING when the rep is short of the fee, ERROR when they have
                 handed out MORE than the team owes — the second is a real
                 over-collection, not a to-do. */
              <span css={subtle} data-testid='summary-differential'>
                <Price amount={allocated} currency={currency} />{' '}
                {balanceText('common:balanceCardAllocatedOf')}{' '}
                <Price amount={teamCost} currency={currency} />
                {' · '}
                <strong
                  css={css`
                    ${weightSemiBold};
                    color: ${overAllocated
                      ? colors.ERROR_200
                      : colors.WARNING_300};
                  `}
                >
                  <Price amount={Math.abs(unallocated)} currency={currency} />{' '}
                  {balanceText(
                    overAllocated
                      ? 'common:balanceCardOverAllocated'
                      : 'common:balanceCardUnallocated'
                  )}
                </strong>
              </span>
            ) : null}
          </>
        ) : (
          <span css={subtle}>
            {balanceLabel('common:balanceSummaryMixedCurrency')}
          </span>
        )}
      </div>
    </div>
  )
}

export { Track }
export default BalanceSummaryCards
